import { inject, Injectable, signal, Signal, WritableSignal } from "@angular/core";
import { BehaviorSubject, distinctUntilChanged, filter, map, Observable, tap } from "rxjs";
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Route, Router, Routes, UrlSegment } from "@angular/router";
import { NavigatedRoute, NavigatedRoutes, NavigationStack } from "../../types/navigation.type";
import { CORE_ROUTES_DEFINITION, DEACTIVATE_PREFIX } from "@core/constants/routes.constant";
import { toSignal } from "@angular/core/rxjs-interop";
import { BreadcrumbDataResolver } from "@core/services/breadcrumb/breadcrumb.type";
import { ExtjsService } from "@core/services/extjs/extjs.service";

@Injectable({
  providedIn: "root"
})
export class NavigationService {
  public navigationStack$: BehaviorSubject<NavigationStack> = new BehaviorSubject<NavigationStack>({
    navigatedRoutes: [],
    paramMap: new Map<string, string>()
  });

  urlSegments: Signal<string[]> = this.createNavLevels();

  private _router: Router = inject(Router);

  private _paramMap: Map<string, string> = new Map<string, string>();

  private _paramMapSignal: WritableSignal<Map<string, string>> = signal(new Map<string, string>());

  getParamMap (): Map<string, string> {
    return this._paramMapSignal();
  }

  constructor () {
    inject(ExtjsService).iframeHash$.pipe(
      tap((hash: string): void => {
        this.buildNavigationStackFromExts([ "", ...hash.split("/") ]);
      })
    ).subscribe();
    this._router.events.pipe(
      /* eslint-disable-next-line @tseslint/typedef */
      filter((navigationEvent) => navigationEvent instanceof NavigationEnd),
      distinctUntilChanged(),
      tap(() => this.buildNavigationStack(this._router.routerState.root))
    ).subscribe();
  }

  private createNavLevels (): Signal<string[]> {
    const navLevel$: Observable<string[]> = this.navigationStack$.pipe(
      map((nav: NavigationStack): string[] =>
        nav.navigatedRoutes.map((route: NavigatedRoute): string => route.instancePath))
    );

    return toSignal(navLevel$, { initialValue: [] });
  }

  private buildNavigationStack (route: ActivatedRoute, paramMap: Map<string, string> = new Map<string, string>(), navigatedRoutes: NavigatedRoutes = []): void {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      const isAngularRouterNavigation: boolean = !navigatedRoutes.some((navRoute: NavigatedRoute) => navRoute.route.path === "**");

      if (isAngularRouterNavigation)
        this._publishNavigationStack(navigatedRoutes, paramMap);


      return;
    }

    for (const child of children) {
      if (child.outlet !== "primary")
        continue;

      const snapshot: ActivatedRouteSnapshot = child.snapshot;
      const segment: string = snapshot.url.map((urlSegment: UrlSegment) => urlSegment.path).filter(Boolean)
        .join("/");
      const routeConfig: Route | null = child.routeConfig;

      if (routeConfig) {
        this._update(paramMap, navigatedRoutes, routeConfig, segment);
        this.buildNavigationStack(child, paramMap, navigatedRoutes);
      }
    }
  }

  public buildNavigationStackFromExts (
    segments: string[],
    routes: Routes = CORE_ROUTES_DEFINITION,
    paramMap: Map<string, string> = new Map<string, string>(),
    navigatedRoutes: NavigatedRoutes = []
  ): void {
    const { route, segment, remainingSegments }: {
      route: Route;
      segment: string | null;
      remainingSegments: string[];
    } = this._findRouteAndRemainingSegments(routes, segments);

    // eslint-disable-next-line @tseslint/no-non-null-assertion
    this._update(paramMap, navigatedRoutes, route, segment!);

    const routeData: BreadcrumbDataResolver | undefined = route.data as BreadcrumbDataResolver | undefined;
    const nextRoutes: Routes | undefined = routeData?.routes?.() ?? route.children;


    if (remainingSegments.length === 0 || routeData?.leaf || !nextRoutes)
      this._publishNavigationStack(navigatedRoutes, paramMap);
    else
      this.buildNavigationStackFromExts(remainingSegments, nextRoutes, paramMap, navigatedRoutes);
  }

  private _findRouteAndRemainingSegments (routes: Routes, segments: string[]): {
    route: Route;
    segment: string | null;
    remainingSegments: string[];
  } {
    let segment: string = "";

    for (let index: number = 0; index < segments.length; index++) {
      // eslint-disable-next-line @tseslint/no-non-null-assertion
      segment = segment ? `${ segment }/${ segments[ index ] }` : segments[ index ]!;

      const route: Route | null = this._findRoute(routes, segment);

      if (route)
        return { route, segment, remainingSegments: segments.slice(index + 1) };
    }

    throw new Error(`No matching route found for segment path: "${ segment }". The route configuration may be incorrect or missing for the given segments: [${ segments.join(", ") }]`);
  }


  private _update (params: Map<string, string>, navigatedRoutes: NavigatedRoutes, routeConfig: Route, segment: string): void {
    if (routeConfig.data?.[ "context" ]) {
      /* TODO: */
    }

    routeConfig.path && this._updateParamMap(routeConfig.path, segment, params);
    navigatedRoutes.push({ route: routeConfig, instancePath: segment });
  }

  private _publishNavigationStack (navigatedRoutes: NavigatedRoutes, paramMap: Map<string, string>): void {
    this._paramMapSignal.set(this._paramMap);
    this.navigationStack$.next({ navigatedRoutes, paramMap });
  }

  private _findRoute (routes: Route[], segment: string): Route | null {
    const matchingRoutes: Route[] = routes.filter((r: Route) => this._pathMatchesSegment(r, segment).match);

    if (matchingRoutes.length === 1)
    // eslint-disable-next-line @tseslint/no-non-null-assertion
      return matchingRoutes[ 0 ]!;

    const exactMatch: Route | undefined = matchingRoutes.find((r: Route) => r.path?.endsWith(segment));

    if (exactMatch)
      return exactMatch.redirectTo ? routes.find((r: Route): boolean => r.path === exactMatch.redirectTo) ?? null : exactMatch;


    for (const route of routes) {
      const childRoute: Route | undefined = route.children?.find((child: Route) => this._pathMatchesSegment(child, segment).match);

      if (childRoute?.path?.startsWith(segment))
        return childRoute;
    }

    return null;
  }

  private _updateParamMap (routePath: string, segment: string, paramMap: Map<string, string>): void {
    const routeSegments: string[] = routePath.split("/"),
      segmentSplit: string[] = segment.split("/");

    routeSegments.forEach((s: string, i: number) => {
      const split: string[] = s.split(DEACTIVATE_PREFIX);
      /* eslint-disable-next-line @tseslint/prefer-nullish-coalescing */
      const path: string | undefined = split[ 1 ] || split[ 0 ];

      if (path?.startsWith(":"))
      /* eslint-disable-next-line @tseslint/no-non-null-assertion  */
        paramMap.set(path.slice(1), segmentSplit[ i ]!);
    });
    this._paramMap = paramMap;
  }


  private _pathMatchesSegment (r: Route, segment: string): { match: boolean; matchArray: RegExpMatchArray | null; } {
    const split: string[] | undefined = r.path?.split(DEACTIVATE_PREFIX);
    const activePathStart: string | undefined = split?.[ 1 ] ?? split?.[ 0 ];
    const regExp: RegExp = this.toRegularExp(activePathStart);
    const matchArray: RegExpMatchArray | null = segment.match(regExp);

    return { match: Boolean(matchArray), matchArray };
  }

  /* eslint-disable-next-line @tseslint/class-methods-use-this */
  private toRegularExp (path: string | undefined): RegExp {
    /* eslint-disable-next-line @tseslint/no-non-null-assertion */
    return new RegExp(path === "**" ? ".*" : `^${ path!.replaceAll(/:[^\s/]+/g, "([^/]+)").replaceAll('/', "\\/") }$`);
  }
}
